import { ENTITY_PIXELS, GAME_HEIGHT, SAFE_AREA } from '../config';
import { ARMY_HARD_CAP, ENEMIES, SCROLL_SPEED, WEAPONS, WeaponType, GAMEPLAY_START } from '../utils/constants';
import { getOrderedSegmentTemplates, SegmentId } from './SegmentTemplates';
import { createRng, createSeed, deterministicRoll, randomInt, Rng } from './generator/deterministic';
import {
  applyGateOperation,
  randomAddOperation,
  randomAddOrSubOperation,
  randomSubOperation,
} from './generator/gateOperations';
import { getCandidateGateRowIndices, getOpeningEnemyCount } from './generator/segmentPlanning';
import { allocateByWeight, computeBestPathMaxArmy, computeExpectedArmyAtProgress } from './generator/eventCompilation';
import {
  getFinaleHp,
  getFinaleSpawnTimeSeconds,
  getGateActivationTimesSeconds,
  getLevelFlowTarget,
  getTargetDurationSeconds,
  getWeaponBoxLatestSpawnTimeSeconds,
} from './gameplayBalance';

export type GateLane = 'left' | 'right';
export type EnemyLane4 = 'farLeft' | 'leftCenter' | 'rightCenter' | 'farRight';

export type GateOperation =
  | { kind: 'mul'; value: 1 | 2 | 3 | 5 }
  | { kind: 'add'; value: number }
  | { kind: 'sub'; value: number };

export interface CompiledEnemyEvent {
  id: number;
  triggerDistance: number;
  segmentId: SegmentId;
  segmentIndex: number;
  lane: EnemyLane4;
  type: 'basic' | 'elite';
  hp?: number;
  /** Finale targets must be defeated; leaking or colliding with one fails the run. */
  isFinale?: boolean;
}

export interface CompiledGateOption {
  lane: GateLane;
  operation: GateOperation;
}

export interface CompiledGateEvent {
  id: number;
  triggerDistance: number;
  segmentId: SegmentId;
  segmentIndex: number;
  options: [CompiledGateOption, CompiledGateOption];
  /** Optional authored start position for the opening gate's short approach. */
  spawnY?: number;
}
type CandidateGateEvent = Omit<CompiledGateEvent, 'id'>;

interface GatePotentialState {
  gate: CompiledGateEvent;
  armyBeforeGate: number;
  bestAfterGate: number;
  maxPotentialUpToGate: number;
  gateGrowth: number;
}

export interface CompiledWeaponBoxEvent {
  id: number;
  triggerDistance: number;
  segmentId: SegmentId;
  segmentIndex: number;
  lane: EnemyLane4;
  chance: number;
  baseHp: number;
  expectedArmy: number;
}

export interface GeneratedSegmentState {
  id: SegmentId;
  index: number;
  startDistance: number;
  endDistance: number;
  expectedArmy: number;
  spawnIntervalSeconds: number;
  eliteRatio: number;
  gatePositivity: number;
}

export interface GeneratedLevelConfig {
  level: number;
  tier: number;
  seed: number;
  trackLength: number;
  targetDurationSeconds: number;
  scrollSpeed: number;
  segments: GeneratedSegmentState[];
  enemyEvents: CompiledEnemyEvent[];
  gateEvents: CompiledGateEvent[];
  weaponBoxEvents: CompiledWeaponBoxEvent[];
  bestPathMaxArmy: number;
  targetEnemyCount: number;
  targetEliteCount: number;
  x2RowIndex: number;
}

const ENEMY_LANES_4: EnemyLane4[] = ['farLeft', 'leftCenter', 'rightCenter', 'farRight'];

const LANE_PATTERNS: EnemyLane4[][] = [
  ['farLeft', 'leftCenter', 'rightCenter', 'farRight'],
  ['leftCenter', 'rightCenter', 'leftCenter', 'rightCenter'],
  ['farLeft', 'rightCenter', 'leftCenter', 'farRight'],
  ['farRight', 'leftCenter', 'rightCenter', 'farLeft'],
  ['leftCenter', 'farLeft', 'rightCenter', 'farRight'],
];

const DEFAULT_TRACK_WIDTH = 335;
const SOLDIER_RADIUS = (48 * 0.38) / 2;
const FORMATION_MIN_RADIUS = Math.max(10, SOLDIER_RADIUS * 1.2);
const FORMATION_MAX_RADIUS = DEFAULT_TRACK_WIDTH * 0.45;
const PLAYER_ARMY_Y = GAME_HEIGHT - SAFE_AREA.bottom - 120;
const ENEMY_SPAWN_Y = -20;
const BASIC_ENEMY_RADIUS = (48 * 0.68) / 2;
const ELITE_ENEMY_RADIUS = BASIC_ENEMY_RADIUS * 1.2;
const DEFAULT_GATE_SPAWN_Y = -ENTITY_PIXELS.gateHeight;
const BULLET_INTERCEPT_LEAD_SECONDS = 1.0;
const BEATABILITY_MIN_SLACK = 0;
const REINFORCEMENT_GROWTH_FACTOR = 0.14;
const REINFORCEMENT_PER_GATE_CAP = 6;

interface BeatabilitySnapshot {
  minSlack: number;
  firstDeficitDueTimeSeconds: number | null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function levelMultiplier(level: number): number {
  return SCROLL_SPEED.levelMultipliers[level] ?? 1;
}

function getScrollSpeedForLevel(level: number, tier: number): number {
  const tierBonus = (tier - 1) * SCROLL_SPEED.tierMultiplier;
  return SCROLL_SPEED.base * (levelMultiplier(level) + tierBonus);
}

function computeWeaponBoxHp(expectedArmy: number): number {
  const expectedDps = expectedArmy * WEAPONS[WeaponType.Pistol].fireRate * 1 * 0.65;
  return clamp(Math.round(expectedDps), 3, 120);
}

function computeGatePotentialStates(
  gateEvents: CompiledGateEvent[],
  startingArmy: number = GAMEPLAY_START.startingSoldiers
): GatePotentialState[] {
  const sortedGates = [...gateEvents].sort((left, right) =>
    left.triggerDistance - right.triggerDistance || left.id - right.id
  );

  const states: GatePotentialState[] = [];
  let army = Math.max(0, startingArmy);
  let maxPotential = army;

  for (const gate of sortedGates) {
    const [left, right] = gate.options;
    const leftOutcome = applyGateOperation(army, left.operation);
    const rightOutcome = applyGateOperation(army, right.operation);
    const bestAfterGate = Math.max(leftOutcome, rightOutcome);
    maxPotential = Math.max(maxPotential, bestAfterGate);

    states.push({
      gate,
      armyBeforeGate: army,
      bestAfterGate,
      maxPotentialUpToGate: maxPotential,
      gateGrowth: Math.max(0, bestAfterGate - army),
    });

    army = bestAfterGate;
  }

  return states;
}

function computeEliteHp(level: number, rng: Rng): number {
  const base = 3 + Math.floor(level * 0.45);
  return clamp(base + randomInt(rng, 0, Math.max(2, Math.floor(level * 0.4))), 5, 18);
}

export function getGateActivationDistance(gate: CompiledGateEvent): number {
  const spawnY = gate.spawnY ?? DEFAULT_GATE_SPAWN_Y;
  const travelUntilCollision = Math.max(
    0,
    PLAYER_ARMY_Y - ENTITY_PIXELS.gateHeight * 0.5 - spawnY
  );
  return gate.triggerDistance + travelUntilCollision;
}

function getArmyAtTimeSeconds(
  timeSeconds: number,
  gatePotentialStates: GatePotentialState[],
  scrollSpeed: number
): number {
  let army: number = GAMEPLAY_START.startingSoldiers;
  for (const state of gatePotentialStates) {
    const gateActivationSeconds = getGateActivationDistance(state.gate) / scrollSpeed;
    if (timeSeconds < gateActivationSeconds) {
      break;
    }
    army = state.bestAfterGate;
  }
  return Math.max(0, army);
}

function getFormationRadiusFromArmy(armyCount: number): number {
  if (armyCount <= 0) return 0;
  const normalized = Math.sqrt(Math.min(1, armyCount / ARMY_HARD_CAP));
  return FORMATION_MIN_RADIUS + (FORMATION_MAX_RADIUS - FORMATION_MIN_RADIUS) * normalized;
}

function getEnemyDueTimeSeconds(
  event: CompiledEnemyEvent,
  gatePotentialStates: GatePotentialState[],
  scrollSpeed: number
): number {
  const spawnTimeSeconds = event.triggerDistance / scrollSpeed;
  const armyAtSpawn = getArmyAtTimeSeconds(spawnTimeSeconds, gatePotentialStates, scrollSpeed);
  const formationRadius = getFormationRadiusFromArmy(armyAtSpawn);
  const enemyRadius = event.type === 'elite' ? ELITE_ENEMY_RADIUS : BASIC_ENEMY_RADIUS;
  const contactY = PLAYER_ARMY_Y - (formationRadius + enemyRadius);
  const travelDistance = Math.max(8, contactY - ENEMY_SPAWN_Y);
  const speedMultiplier = event.type === 'elite' ? ENEMIES.elite.speed : ENEMIES.basic.speed;
  const enemySpeed = Math.max(1, scrollSpeed * speedMultiplier);
  return spawnTimeSeconds + travelDistance / enemySpeed;
}

function getShotCapacityUntilSeconds(
  timeSeconds: number,
  gatePotentialStates: GatePotentialState[],
  scrollSpeed: number,
  shotsPerSoldierPerSecond: number
): number {
  const clampedTimeSeconds = Math.max(0, timeSeconds);
  if (clampedTimeSeconds <= 0 || shotsPerSoldierPerSecond <= 0) return 0;

  let capacity = 0;
  let army: number = GAMEPLAY_START.startingSoldiers;
  let cursorSeconds = 0;

  for (const state of gatePotentialStates) {
    const gateActivationSeconds = getGateActivationDistance(state.gate) / scrollSpeed;
    if (clampedTimeSeconds <= gateActivationSeconds) {
      capacity += Math.max(0, clampedTimeSeconds - cursorSeconds) * army * shotsPerSoldierPerSecond;
      return capacity;
    }
    capacity += Math.max(0, gateActivationSeconds - cursorSeconds) * army * shotsPerSoldierPerSecond;
    army = state.bestAfterGate;
    cursorSeconds = gateActivationSeconds;
  }

  capacity += Math.max(0, clampedTimeSeconds - cursorSeconds) * army * shotsPerSoldierPerSecond;
  return capacity;
}

function evaluateBeatability(
  enemyEvents: CompiledEnemyEvent[],
  gatePotentialStates: GatePotentialState[],
  scrollSpeed: number,
  shotsPerSoldierPerSecond: number,
  dueTimeById: Map<number, number>
): BeatabilitySnapshot {
  const demands = enemyEvents
    .map((event) => ({
      event,
      dueTimeSeconds: dueTimeById.get(event.id) ??
        getEnemyDueTimeSeconds(event, gatePotentialStates, scrollSpeed),
      hp: event.type === 'elite' ? Math.max(1, event.hp ?? 1) : 1,
    }))
    .sort((left, right) =>
      left.dueTimeSeconds - right.dueTimeSeconds ||
      left.event.triggerDistance - right.event.triggerDistance ||
      left.event.id - right.event.id
    );

  let cumulativeHpDemand = 0;
  let minSlack = Number.POSITIVE_INFINITY;
  let firstDeficitDueTimeSeconds: number | null = null;

  for (const demand of demands) {
    cumulativeHpDemand += demand.hp;
    const shotCapacity = getShotCapacityUntilSeconds(
      demand.dueTimeSeconds - BULLET_INTERCEPT_LEAD_SECONDS,
      gatePotentialStates,
      scrollSpeed,
      shotsPerSoldierPerSecond
    );
    const slack = shotCapacity - cumulativeHpDemand;
    minSlack = Math.min(minSlack, slack);
    if (firstDeficitDueTimeSeconds === null && slack < BEATABILITY_MIN_SLACK) {
      firstDeficitDueTimeSeconds = demand.dueTimeSeconds;
    }
  }

  return {
    minSlack: Number.isFinite(minSlack) ? minSlack : 0,
    firstDeficitDueTimeSeconds,
  };
}

function enforceBeatabilityGuardrail(
  enemyEvents: CompiledEnemyEvent[],
  reinforcementEventIds: Set<number>,
  gatePotentialStates: GatePotentialState[],
  scrollSpeed: number,
  protectedDistance: number,
  assumedHitRate: number
): void {
  const shotsPerSoldierPerSecond = Math.max(
    0.1,
    WEAPONS[WeaponType.Pistol].fireRate * clamp(assumedHitRate, 0.05, 1)
  );
  const dueTimeById = new Map<number, number>();
  for (const event of enemyEvents) {
    dueTimeById.set(event.id, getEnemyDueTimeSeconds(event, gatePotentialStates, scrollSpeed));
  }

  const removeEventById = (eventId: number): boolean => {
    const index = enemyEvents.findIndex((event) => event.id === eventId);
    if (index < 0) return false;
    enemyEvents.splice(index, 1);
    reinforcementEventIds.delete(eventId);
    dueTimeById.delete(eventId);
    return true;
  };

  const byTriggerThenId = (left: CompiledEnemyEvent, right: CompiledEnemyEvent): number =>
    left.triggerDistance - right.triggerDistance || left.id - right.id;

  const maxIterations = Math.max(64, enemyEvents.length * 4);
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const snapshot = evaluateBeatability(
      enemyEvents,
      gatePotentialStates,
      scrollSpeed,
      shotsPerSoldierPerSecond,
      dueTimeById
    );
    if (snapshot.minSlack >= BEATABILITY_MIN_SLACK) {
      return;
    }

    const dueCutoff = snapshot.firstDeficitDueTimeSeconds;
    const isProtectedEvent = (event: CompiledEnemyEvent): boolean =>
      event.triggerDistance <= protectedDistance + 1e-6;
    const isInDeficitWindow = (event: CompiledEnemyEvent): boolean => {
      if (dueCutoff === null) return true;
      const dueTime = dueTimeById.get(event.id) ?? Number.POSITIVE_INFINITY;
      return dueTime <= dueCutoff + 1e-6;
    };

    const reinforcementCandidate = enemyEvents
      .filter((event) => !isProtectedEvent(event) && reinforcementEventIds.has(event.id) && isInDeficitWindow(event))
      .sort(byTriggerThenId)[0];
    if (reinforcementCandidate && removeEventById(reinforcementCandidate.id)) {
      continue;
    }

    const eliteCandidate = enemyEvents
      .filter((event) => !isProtectedEvent(event) && event.type === 'elite' && isInDeficitWindow(event))
      .sort((left, right) =>
        (right.hp ?? 1) - (left.hp ?? 1) ||
        left.triggerDistance - right.triggerDistance ||
        left.id - right.id
      )[0];
    if (eliteCandidate) {
      eliteCandidate.type = 'basic';
      eliteCandidate.hp = undefined;
      continue;
    }

    const basicCandidate = enemyEvents
      .filter((event) => !isProtectedEvent(event) && event.type === 'basic' && isInDeficitWindow(event))
      .sort(byTriggerThenId)[0];
    if (basicCandidate && removeEventById(basicCandidate.id)) {
      continue;
    }

    return;
  }
}

export function getEnemyLane4X(
  lane: EnemyLane4,
  trackWidth: number = 335,
  trackPadding: number = 20
): number {
  const normalizedByLane: Record<EnemyLane4, number> = {
    farLeft: 0.14,
    leftCenter: 0.37,
    rightCenter: 0.63,
    farRight: 0.86,
  };
  const normalized = normalizedByLane[lane];
  return trackPadding + trackWidth * normalized;
}

function scheduleGateResolutionBeats(
  gateEvents: CompiledGateEvent[],
  scrollSpeed: number,
  durationSeconds: number
): void {
  const activationTimes = getGateActivationTimesSeconds(gateEvents.length, durationSeconds);
  const defaultTravelDistance =
    PLAYER_ARMY_Y - ENTITY_PIXELS.gateHeight * 0.5 - DEFAULT_GATE_SPAWN_Y;

  for (let index = 0; index < gateEvents.length; index++) {
    const gate = gateEvents[index];
    const desiredActivationDistance = Math.round(scrollSpeed * activationTimes[index]);
    const minimumTriggerDistance = 12;

    if (desiredActivationDistance <= defaultTravelDistance + minimumTriggerDistance) {
      gate.triggerDistance = minimumTriggerDistance;
      const authoredTravelDistance = Math.max(1, desiredActivationDistance - minimumTriggerDistance);
      gate.spawnY = Math.round(
        PLAYER_ARMY_Y - ENTITY_PIXELS.gateHeight * 0.5 - authoredTravelDistance
      );
    } else {
      gate.triggerDistance = Math.max(
        minimumTriggerDistance,
        Math.round(desiredActivationDistance - defaultTravelDistance)
      );
      delete gate.spawnY;
    }
  }
}

function normalizeGateChoices(
  gateEvents: CompiledGateEvent[],
  level: number,
  rng: Rng
): void {
  let expectedArmy: number = GAMEPLAY_START.startingSoldiers;

  for (let rowIndex = 0; rowIndex < gateEvents.length; rowIndex++) {
    const gate = gateEvents[rowIndex];
    const x2OptionIndex = gate.options.findIndex(
      (option) => option.operation.kind === 'mul' && option.operation.value === 2
    );

    if (rowIndex === 0) {
      const first = randomAddOperation(level, rng);
      let second = randomAddOperation(level, rng);
      if (second.value === first.value) {
        second = { kind: 'add', value: first.value + 1 };
      }
      gate.options[0].operation = first;
      gate.options[1].operation = second;
    } else if (x2OptionIndex >= 0) {
      const addOptionIndex = x2OptionIndex === 0 ? 1 : 0;
      let addOperation = randomAddOperation(level, rng);
      const doubledArmy = applyGateOperation(expectedArmy, { kind: 'mul', value: 2 });
      if (applyGateOperation(expectedArmy, addOperation) === doubledArmy) {
        addOperation = { kind: 'add', value: addOperation.value + 1 };
      }
      gate.options[addOptionIndex].operation = addOperation;
    } else {
      const positiveOptionIndex = rng.next() < 0.5 ? 0 : 1;
      const otherOptionIndex = positiveOptionIndex === 0 ? 1 : 0;
      const positive = randomAddOperation(level, rng);
      gate.options[positiveOptionIndex].operation = positive;

      const canOfferLoss = level >= 2 && expectedArmy >= 5 && rng.next() < 0.35;
      if (canOfferLoss) {
        const rawLoss = randomSubOperation(level, rng).value;
        const maxFairLoss = Math.max(1, Math.min(expectedArmy - 1, Math.floor(expectedArmy * 0.35)));
        gate.options[otherOptionIndex].operation = {
          kind: 'sub',
          value: Math.min(rawLoss, maxFairLoss),
        };
      } else {
        let alternate = randomAddOperation(level, rng);
        if (alternate.value === positive.value) {
          alternate = { kind: 'add', value: positive.value + 1 };
        }
        gate.options[otherOptionIndex].operation = alternate;
      }
    }

    const outcomes = gate.options.map((option) => applyGateOperation(expectedArmy, option.operation));
    expectedArmy = Math.max(...outcomes);
  }
}

interface AuthoredLevelOnePlan {
  enemyEvents: CompiledEnemyEvent[];
  gateEvents: CompiledGateEvent[];
  weaponBoxEvents: CompiledWeaponBoxEvent[];
  bestPathMaxArmy: number;
  x2RowIndex: number;
}

function buildAuthoredLevelOnePlan(
  segments: GeneratedSegmentState[],
  scrollSpeed: number,
  durationSeconds: number,
  tier: number
): AuthoredLevelOnePlan {
  const getSegmentForTime = (timeSeconds: number): GeneratedSegmentState => {
    const distance = timeSeconds * scrollSpeed;
    return segments.find((segment) => distance <= segment.endDistance) ?? segments[segments.length - 1];
  };

  let nextId = 1;
  const gateEvents: CompiledGateEvent[] = [
    {
      id: nextId++,
      triggerDistance: 0,
      segmentId: 'onboard',
      segmentIndex: 0,
      options: [
        { lane: 'left', operation: { kind: 'add', value: 3 } },
        { lane: 'right', operation: { kind: 'add', value: 4 } },
      ],
    },
    {
      id: nextId++,
      triggerDistance: 0,
      segmentId: 'pressureA',
      segmentIndex: 2,
      options: [
        { lane: 'left', operation: { kind: 'add', value: 4 } },
        { lane: 'right', operation: { kind: 'mul', value: 2 } },
      ],
    },
    {
      id: nextId++,
      triggerDistance: 0,
      segmentId: 'peak',
      segmentIndex: 4,
      options: [
        { lane: 'left', operation: { kind: 'add', value: 6 } },
        { lane: 'right', operation: { kind: 'add', value: 4 } },
      ],
    },
  ];
  scheduleGateResolutionBeats(gateEvents, scrollSpeed, durationSeconds);

  const bestPathMaxArmy = computeBestPathMaxArmy(gateEvents, GAMEPLAY_START.startingSoldiers);
  const enemyEvents: CompiledEnemyEvent[] = [];
  const addEnemy = (
    timeSeconds: number,
    lane: EnemyLane4,
    type: 'basic' | 'elite' = 'basic',
    hp?: number,
    isFinale: boolean = false
  ): void => {
    const segment = getSegmentForTime(timeSeconds);
    enemyEvents.push({
      id: nextId++,
      triggerDistance: Math.round(timeSeconds * scrollSpeed),
      segmentId: segment.id,
      segmentIndex: segment.index,
      lane,
      type,
      hp,
      isFinale: isFinale || undefined,
    });
  };

  // Two readable opening targets, then alternating lane bursts. The gaps are
  // deliberate recovery beats rather than generator dead time.
  addEnemy(0.5, 'leftCenter');
  addEnemy(1.4, 'rightCenter');

  [4.9, 5.2, 5.5].forEach((time) => addEnemy(time, 'leftCenter'));
  [7.2, 7.5, 7.8].forEach((time) => addEnemy(time, 'rightCenter'));

  const pressureLanes: EnemyLane4[] = ['farLeft', 'leftCenter', 'farLeft', 'leftCenter', 'rightCenter'];
  [16.9, 17.15, 17.4, 17.65, 17.9].forEach((time, index) => addEnemy(time, pressureLanes[index]));
  [20.0, 20.25, 20.5, 20.75, 21.0].forEach((time, index) =>
    addEnemy(time, index % 2 === 0 ? 'rightCenter' : 'farRight')
  );
  addEnemy(22.4, 'farLeft');

  [23.7, 23.95, 24.2, 24.45, 25.3, 25.55, 25.8, 26.05].forEach((time, index) =>
    addEnemy(time, index % 2 === 0 ? 'leftCenter' : 'rightCenter')
  );
  addEnemy(27.1, 'farRight');

  [28.0, 28.25, 28.5, 30.0, 30.25, 30.5].forEach((time, index) =>
    addEnemy(time, index < 3 ? 'leftCenter' : 'farRight')
  );

  addEnemy(
    getFinaleSpawnTimeSeconds(durationSeconds),
    'rightCenter',
    'elite',
    Math.min(52, getFinaleHp(1, bestPathMaxArmy, tier)),
    true
  );

  const weaponSegment = getSegmentForTime(10.5);
  const weaponBoxEvents: CompiledWeaponBoxEvent[] = [{
    id: nextId++,
    triggerDistance: Math.round(10.5 * scrollSpeed),
    segmentId: weaponSegment.id,
    segmentIndex: weaponSegment.index,
    lane: 'leftCenter',
    chance: 1,
    baseHp: 8,
    expectedArmy: 5,
  }];

  return {
    enemyEvents,
    gateEvents,
    weaponBoxEvents,
    bestPathMaxArmy,
    x2RowIndex: 1,
  };
}

export function createDeterministicLevelSeed(level: number, tier: number): number {
  return createSeed(level, tier);
}

export function generateLevelConfig(level: number, tier: number = 1, seed?: number): GeneratedLevelConfig {
  const normalizedLevel = clamp(Math.round(level), 1, 10);
  const normalizedTier = Math.max(1, Math.round(tier));
  const resolvedSeed = typeof seed === 'number' ? seed : createDeterministicLevelSeed(normalizedLevel, normalizedTier);
  const rng = createRng(resolvedSeed);

  const scrollSpeed = getScrollSpeedForLevel(normalizedLevel, normalizedTier);
  const targetDurationSeconds = getTargetDurationSeconds(normalizedLevel);
  const trackLength = Math.round(scrollSpeed * targetDurationSeconds);
  const openingWindowDistance = scrollSpeed * 6;

  const rawSpawnInterval = Math.max(0.25, 2.8 - 0.22 * (normalizedLevel - 1));
  const lateLevelRelief = normalizedLevel >= 8 ? (normalizedLevel - 7) * 0.32 : 0;
  const levelOnePressure = normalizedLevel === 1 ? 0.92 : 1;
  const midLevelPressure = normalizedLevel >= 4 && normalizedLevel <= 6 ? 0.97 : 1;
  const baseSpawnInterval = (rawSpawnInterval + lateLevelRelief) * levelOnePressure * midLevelPressure;

  let baseEliteRatio = clamp(0.03 * (normalizedLevel - 1), 0, 0.28);
  if (normalizedLevel >= 4 && normalizedLevel <= 6) {
    baseEliteRatio *= 1.04;
  }
  if (normalizedLevel >= 8) {
    baseEliteRatio *= 0.5;
  }

  let baseGatePositivity = clamp(0.88 - 0.03 * (normalizedLevel - 1), 0.62, 0.88);
  if (normalizedLevel >= 8) {
    baseGatePositivity = clamp(baseGatePositivity + 0.18, 0.62, 0.95);
  }
  if (normalizedLevel >= 4 && normalizedLevel <= 6) {
    baseGatePositivity = clamp(baseGatePositivity - 0.02, 0.62, 0.88);
  }
  if (normalizedLevel === 1) {
    baseGatePositivity = clamp(baseGatePositivity - 0.05, 0.62, 0.88);
  }

  const templates = getOrderedSegmentTemplates();
  const segments: GeneratedSegmentState[] = [];
  let cursor = 0;

  for (let i = 0; i < templates.length; i++) {
    const template = templates[i];
    const isLast = i === templates.length - 1;
    const segmentLength = isLast
      ? trackLength - cursor
      : Math.max(120, Math.round(trackLength * template.durationWeight));
    const startDistance = cursor;
    const endDistance = isLast ? trackLength : Math.min(trackLength, cursor + segmentLength);
    const midProgress = clamp((startDistance + endDistance) / 2 / trackLength, 0, 1);

    let spawnIntervalSeconds = baseSpawnInterval * template.spawnIntervalMultiplier;
    let eliteRatio = clamp(baseEliteRatio * template.eliteRatioMultiplier, 0, 0.35);

    if (normalizedLevel >= 8 && (template.id === 'peak' || template.id === 'finale')) {
      spawnIntervalSeconds *= 1.3;
      eliteRatio *= 0.65;
    }

    segments.push({
      id: template.id,
      index: i,
      startDistance,
      endDistance,
      expectedArmy: clamp(
        GAMEPLAY_START.startingSoldiers + normalizedLevel * 0.6 + midProgress * (14 + normalizedLevel * 3.4),
        GAMEPLAY_START.startingSoldiers,
        ARMY_HARD_CAP
      ),
      spawnIntervalSeconds,
      eliteRatio: clamp(eliteRatio, 0, 0.35),
      gatePositivity: clamp(baseGatePositivity - template.gateTrapBias, 0.45, 0.95),
    });

    cursor = endDistance;
  }

  let eventId = 1;
  const enemyEvents: CompiledEnemyEvent[] = [];
  const gateEvents: CompiledGateEvent[] = [];
  const candidateGateEvents: CandidateGateEvent[] = [];
  const weaponBoxEvents: CompiledWeaponBoxEvent[] = [];

  for (const segment of segments) {
    const template = templates[segment.index];
    const segmentLength = segment.endDistance - segment.startDistance;

    for (let row = 0; row < template.gateRows; row++) {
      const triggerDistance = segment.startDistance + segmentLength * ((row + 1) / (template.gateRows + 1));

      let operationA = randomAddOrSubOperation(normalizedLevel, rng);
      let operationB = randomAddOrSubOperation(normalizedLevel, rng);

      if (operationA.kind === 'sub' && operationB.kind === 'sub') {
        if (rng.next() < 0.5) {
          operationA = randomAddOperation(normalizedLevel, rng);
        } else {
          operationB = randomAddOperation(normalizedLevel, rng);
        }
      }

      const swapLanes = rng.next() < 0.5;
      candidateGateEvents.push({
        triggerDistance,
        segmentId: segment.id,
        segmentIndex: segment.index,
        options: swapLanes
          ? [
              { lane: 'right', operation: operationA },
              { lane: 'left', operation: operationB },
            ]
          : [
              { lane: 'left', operation: operationA },
              { lane: 'right', operation: operationB },
            ],
      });
    }
  }

  const selectedCandidateIndices = getCandidateGateRowIndices(candidateGateEvents.length, normalizedLevel);
  const selectedGateEvents = selectedCandidateIndices
    .map((candidateIndex) => candidateGateEvents[candidateIndex])
    .filter((event): event is CandidateGateEvent => Boolean(event));
  const x2TimingBias = normalizedLevel <= 6 ? 0.5 : 0.75;
  let x2RowIndex = clamp(
    Math.floor(selectedGateEvents.length * x2TimingBias),
    0,
    Math.max(0, selectedGateEvents.length - 1)
  );

  for (let realizedRowIndex = 0; realizedRowIndex < selectedGateEvents.length; realizedRowIndex++) {
    const sourceEvent = selectedGateEvents[realizedRowIndex];
    const options: [CompiledGateOption, CompiledGateOption] = [
      {
        lane: sourceEvent.options[0].lane,
        operation: sourceEvent.options[0].operation,
      },
      {
        lane: sourceEvent.options[1].lane,
        operation: sourceEvent.options[1].operation,
      },
    ];

    if (realizedRowIndex === x2RowIndex) {
      const x2OptionIndex = rng.next() < 0.5 ? 0 : 1;
      options[x2OptionIndex] = {
        lane: options[x2OptionIndex].lane,
        operation: { kind: 'mul', value: 2 },
      };
    }

    gateEvents.push({
      id: eventId++,
      // Surface the first decision immediately. With a one-soldier start, a
      // mid-segment gate reached the army after opening enemies and turned one
      // missed shot into an instant loss on higher levels.
      triggerDistance: realizedRowIndex === 0
        ? Math.max(12, Math.round(scrollSpeed * 0.15))
        : sourceEvent.triggerDistance,
      segmentId: sourceEvent.segmentId,
      segmentIndex: sourceEvent.segmentIndex,
      options,
    });
  }

  normalizeGateChoices(gateEvents, normalizedLevel, rng);
  scheduleGateResolutionBeats(gateEvents, scrollSpeed, targetDurationSeconds);

  let bestPathMaxArmy = computeBestPathMaxArmy(gateEvents, GAMEPLAY_START.startingSoldiers);
  let gatePotentialStates = computeGatePotentialStates(gateEvents, GAMEPLAY_START.startingSoldiers);
  const baseTargetEnemyCount = Math.round(34 + normalizedLevel * 4.8 + bestPathMaxArmy * 1.45);
  const earlyLevelEnemyRelief = normalizedLevel <= 2
    ? 0.62
    : normalizedLevel <= 4
      ? 0.72
      : normalizedLevel <= 6
      ? 0.8
        : normalizedLevel <= 8
          ? 0.78
          : 0.94;
  const minEnemyBudget = normalizedLevel <= 6
    ? 46 + normalizedLevel * 5
    : normalizedLevel <= 8
      ? 68 + normalizedLevel * 3
    : 66 + normalizedLevel * 5;
  let targetEnemyCount = clamp(
    Math.round(baseTargetEnemyCount * earlyLevelEnemyRelief),
    minEnemyBudget,
    340 + normalizedLevel * 10
  );

  const openingEnemyCount = getOpeningEnemyCount(normalizedLevel, rng);
  const remainingEnemiesBase = Math.max(0, targetEnemyCount - openingEnemyCount);
  const remainingEnemies = Math.round(remainingEnemiesBase * 1.4);
  targetEnemyCount = openingEnemyCount + remainingEnemies;
  const computedEliteRatio = clamp(
    0.04 + (normalizedLevel - 4) * 0.012 + bestPathMaxArmy / 2600,
    0.04,
    0.14
  );
  let targetEliteCount = normalizedLevel < ENEMIES.elite.introLevel
    ? 0
    : Math.max(0, Math.min(targetEnemyCount - openingEnemyCount, Math.round(targetEnemyCount * computedEliteRatio)));

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const midProgress = clamp((segment.startDistance + segment.endDistance) / 2 / trackLength, 0, 1);
    segment.expectedArmy = computeExpectedArmyAtProgress(
      bestPathMaxArmy,
      midProgress,
      GAMEPLAY_START.startingSoldiers
    );
  }

  const firstSegment = segments[0];
  const openingStart = Math.max(firstSegment.startDistance + scrollSpeed * 0.45, 40);
  const openingGateActivationDistance = gateEvents[0]
    ? getGateActivationDistance(gateEvents[0])
    : Number.POSITIVE_INFINITY;
  const openingEndUpperBound = Math.max(
    openingStart + 24,
    Math.min(
      openingWindowDistance - 52,
      firstSegment.endDistance - 180,
      openingGateActivationDistance - 24
    )
  );
  const openingEnd = clamp(
    openingEndUpperBound,
    openingStart + 24,
    openingEndUpperBound
  );

  for (let i = 0; i < openingEnemyCount; i++) {
    const t = (i + 1) / (openingEnemyCount + 1);
    const baseDistance = openingStart + (openingEnd - openingStart) * t;
    const jitter = randomInt(rng, -18, 18);
    const triggerDistance = clamp(baseDistance + jitter, openingStart, openingEnd);

    enemyEvents.push({
      id: eventId++,
      triggerDistance,
      segmentId: firstSegment.id,
      segmentIndex: firstSegment.index,
      lane: ENEMY_LANES_4[i % ENEMY_LANES_4.length],
      type: 'basic',
    });
  }

  const getSegmentForDistance = (distance: number): GeneratedSegmentState => {
    for (const segment of segments) {
      if (distance <= segment.endDistance) {
        return segment;
      }
    }
    return segments[segments.length - 1];
  };

  const stageStartBoundaries: number[] = [openingEnd];
  for (const gate of gatePotentialStates) {
    const activationDistance = getGateActivationDistance(gate.gate);
    if (activationDistance > openingEnd && activationDistance < trackLength) {
      // A gate's benefit begins when the army reaches it, not when it first
      // enters at the top of the screen. Using spawn distance here allowed
      // post-gate waves to attack a one-soldier army before the gate activated.
      stageStartBoundaries.push(activationDistance);
    }
  }

  const stageWeights: number[] = [];
  const stageRanges: Array<{
    index: number;
    start: number;
    end: number;
    potentialStart: number;
    potentialPeak: number;
    potentialGrowth: number;
  }> = [];
  const totalStageSpan = Math.max(1, trackLength - openingEnd);

  for (let stageIndex = 0; stageIndex < stageStartBoundaries.length; stageIndex++) {
    const start = stageStartBoundaries[stageIndex];
    const end = stageStartBoundaries[stageIndex + 1] ?? trackLength;
    const stageLength = Math.max(1, end - start);

    const potentialStart = stageIndex === 0
      ? GAMEPLAY_START.startingSoldiers
      : gatePotentialStates[Math.min(stageIndex - 1, gatePotentialStates.length - 1)]?.maxPotentialUpToGate ??
        GAMEPLAY_START.startingSoldiers;
    const potentialPeak = stageIndex === 0
      ? gatePotentialStates[0]?.maxPotentialUpToGate ?? potentialStart
      : gatePotentialStates[Math.min(stageIndex, gatePotentialStates.length - 1)]?.maxPotentialUpToGate ?? potentialStart;
    const potentialGrowth = Math.max(0, potentialPeak - potentialStart);

    const lengthWeight = stageLength / totalStageSpan;
    const potentialStartNorm = clamp(potentialStart / ARMY_HARD_CAP, 0, 1);
    const potentialPeakNorm = clamp(potentialPeak / ARMY_HARD_CAP, 0, 1);
    const growthNorm = clamp(potentialGrowth / ARMY_HARD_CAP, 0, 1);
    const weight = lengthWeight * (
      1 +
      0.9 * potentialStartNorm +
      1.1 * potentialPeakNorm +
      0.8 * growthNorm
    );

    stageRanges.push({
      index: stageIndex,
      start,
      end,
      potentialStart,
      potentialPeak,
      potentialGrowth,
    });
    stageWeights.push(weight);
  }

  const rawReinforcementByGate = gatePotentialStates.map((state) =>
    clamp(Math.round(state.gateGrowth * REINFORCEMENT_GROWTH_FACTOR), 0, REINFORCEMENT_PER_GATE_CAP)
  );
  const rawReinforcementTotal = rawReinforcementByGate.reduce((sum, value) => sum + value, 0);
  const maxReinforcementBudget = Math.max(0, remainingEnemies - targetEliteCount);
  const reinforcementBudget = Math.min(remainingEnemies, maxReinforcementBudget, rawReinforcementTotal);
  const reinforcementByGate = reinforcementBudget > 0 && rawReinforcementTotal > 0
    ? allocateByWeight(reinforcementBudget, rawReinforcementByGate)
    : new Array(rawReinforcementByGate.length).fill(0);

  const baseStageBudget = Math.max(0, remainingEnemies - reinforcementBudget);
  const baseEnemiesByStage = allocateByWeight(baseStageBudget, stageWeights);

  let nonOpeningSlotsRemaining = baseStageBudget;
  let elitesRemaining = Math.min(targetEliteCount, baseStageBudget);
  const firstGateActivationDistance = gatePotentialStates[0]
    ? getGateActivationDistance(gatePotentialStates[0].gate)
    : openingEnd;
  // Elites are formation checks, so they belong after the player has had time
  // to claim both initial growth and a weapon opportunity. Seeded levels 6 and
  // 8 previously clustered elites near 30% and were harder than levels 7/9.
  const eliteUnlockDistance = Math.max(firstGateActivationDistance, trackLength * 0.38);

  for (const stage of stageRanges) {
    const spawnCount = baseEnemiesByStage[stage.index] ?? 0;
    if (spawnCount <= 0) continue;

    const lanePattern = LANE_PATTERNS[
      (stage.index + randomInt(rng, 0, LANE_PATTERNS.length - 1)) % LANE_PATTERNS.length
    ];
    let laneIndex = randomInt(rng, 0, lanePattern.length - 1);

    let spawnStart = stage.start + 28;
    if (stage.index === 0) {
      spawnStart = Math.max(spawnStart, openingWindowDistance + 18);
    }
    let spawnEnd = stage.end - 42;

    if (spawnEnd <= spawnStart) {
      spawnStart = stage.start + 6;
      spawnEnd = Math.max(spawnStart + 1, stage.end - 6);
    }

    const spawnSpan = Math.max(1, spawnEnd - spawnStart);
    const jitterSize = Math.min(32, spawnSpan / Math.max(4, spawnCount + 1));

    for (let i = 0; i < spawnCount; i++) {
      const t = (i + 1) / (spawnCount + 1);
      const baseDistance = spawnStart + spawnSpan * t;
      const jitter = (rng.next() * 2 - 1) * jitterSize;
      const triggerDistance = clamp(baseDistance + jitter, spawnStart, spawnEnd);
      const owningSegment = getSegmentForDistance(triggerDistance);

      const mustUseElite = elitesRemaining > 0 && elitesRemaining >= nonOpeningSlotsRemaining;
      const canUseElite =
        normalizedLevel >= ENEMIES.elite.introLevel &&
        triggerDistance >= eliteUnlockDistance &&
        elitesRemaining > 0 &&
        nonOpeningSlotsRemaining > 0;
      const shouldUseElite = canUseElite && (mustUseElite || rng.next() < elitesRemaining / nonOpeningSlotsRemaining);

      enemyEvents.push({
        id: eventId++,
        triggerDistance,
        segmentId: owningSegment.id,
        segmentIndex: owningSegment.index,
        lane: lanePattern[laneIndex % lanePattern.length],
        type: shouldUseElite ? 'elite' : 'basic',
        hp: shouldUseElite ? computeEliteHp(normalizedLevel, rng) : undefined,
      });

      if (shouldUseElite) {
        elitesRemaining -= 1;
      }
      nonOpeningSlotsRemaining -= 1;
      laneIndex += 1;
    }
  }

  const reinforcementEventIds = new Set<number>();
  for (let gateIndex = 0; gateIndex < gatePotentialStates.length; gateIndex++) {
    const reinforcementCount = reinforcementByGate[gateIndex] ?? 0;
    if (reinforcementCount <= 0) continue;

    const gateState = gatePotentialStates[gateIndex];
    const gateActivationDistance = getGateActivationDistance(gateState.gate);
    const nextActivationBoundary = gatePotentialStates[gateIndex + 1]
      ? getGateActivationDistance(gatePotentialStates[gateIndex + 1].gate)
      : trackLength;
    if (gateActivationDistance >= trackLength - 24) continue;

    let spawnStart = Math.max(gateActivationDistance + 24, openingWindowDistance + 24);
    let spawnEnd = Math.min(gateActivationDistance + 220, nextActivationBoundary - 30, trackLength - 8);

    if (spawnEnd <= spawnStart) {
      spawnEnd = Math.max(spawnStart + 1, Math.min(nextActivationBoundary - 8, trackLength - 4));
    }

    const lanePattern = LANE_PATTERNS[
      (gateIndex + randomInt(rng, 0, LANE_PATTERNS.length - 1)) % LANE_PATTERNS.length
    ];
    let laneIndex = randomInt(rng, 0, lanePattern.length - 1);
    const spawnSpan = Math.max(1, spawnEnd - spawnStart);

    for (let i = 0; i < reinforcementCount; i++) {
      const t = (i + 1) / (reinforcementCount + 1);
      const baseDistance = spawnStart + spawnSpan * t;
      const jitter = (rng.next() * 2 - 1) * Math.min(20, spawnSpan / Math.max(3, reinforcementCount + 1));
      const triggerDistance = clamp(baseDistance + jitter, spawnStart, spawnEnd);
      const owningSegment = getSegmentForDistance(triggerDistance);

      const reinforcementEvent: CompiledEnemyEvent = {
        id: eventId++,
        triggerDistance,
        segmentId: owningSegment.id,
        segmentIndex: owningSegment.index,
        lane: lanePattern[laneIndex % lanePattern.length],
        type: 'basic',
      };
      enemyEvents.push(reinforcementEvent);
      reinforcementEventIds.add(reinforcementEvent.id);

      laneIndex += 1;
    }
  }

  if (elitesRemaining > 0) {
    const eliteBackfillCandidates = enemyEvents
      .filter((event) =>
        event.type === 'basic' &&
        event.triggerDistance > openingEnd &&
        event.triggerDistance >= eliteUnlockDistance
      )
      .sort((left, right) => right.triggerDistance - left.triggerDistance || left.id - right.id);

    for (const candidate of eliteBackfillCandidates) {
      if (elitesRemaining <= 0) break;
      candidate.type = 'elite';
      candidate.hp = computeEliteHp(normalizedLevel, rng);
      elitesRemaining -= 1;
    }
  }

  // Preserve elite encounters as readable beats instead of allowing the
  // probabilistic assignment/backfill passes to stack several tank enemies on
  // one screen. Dense basic swarms still carry the pressure between elites.
  const eliteSpacingDistance = scrollSpeed * 1.8;
  const elitesByDistance = enemyEvents
    .filter((event) => event.type === 'elite')
    .sort((left, right) => left.triggerDistance - right.triggerDistance || left.id - right.id);
  let lastEliteDistance = Number.NEGATIVE_INFINITY;
  for (const elite of elitesByDistance) {
    if (elite.triggerDistance - lastEliteDistance < eliteSpacingDistance) {
      elite.type = 'basic';
      elite.hp = undefined;
      continue;
    }
    lastEliteDistance = elite.triggerDistance;
  }

  const firstGateDistance = gateEvents.length > 0
    ? Math.min(...gateEvents.map((gate) => getGateActivationDistance(gate)))
    : Number.POSITIVE_INFINITY;
  if (Number.isFinite(firstGateDistance)) {
    const preFirstGateEnemyCap = openingEnemyCount;
    const preFirstGateEnemyCount = enemyEvents.filter((event) => event.triggerDistance < firstGateDistance).length;
    let excessPreFirstGateEnemies = Math.max(0, preFirstGateEnemyCount - preFirstGateEnemyCap);

    if (excessPreFirstGateEnemies > 0) {
      const removableBeforeFirstGate = enemyEvents
        .filter((event) => event.triggerDistance < firstGateDistance && event.triggerDistance > openingEnd)
        .sort((left, right) => left.triggerDistance - right.triggerDistance || left.id - right.id);

      for (const candidate of removableBeforeFirstGate) {
        if (excessPreFirstGateEnemies <= 0) break;
        const index = enemyEvents.findIndex((event) => event.id === candidate.id);
        if (index < 0) continue;
        enemyEvents.splice(index, 1);
        reinforcementEventIds.delete(candidate.id);
        excessPreFirstGateEnemies -= 1;
      }
    }
  }

  enforceBeatabilityGuardrail(
    enemyEvents,
    reinforcementEventIds,
    gatePotentialStates,
    scrollSpeed,
    openingEnd,
    getLevelFlowTarget(normalizedLevel).assumedHitRate
  );
  targetEnemyCount = enemyEvents.length;
  targetEliteCount = enemyEvents.filter((event) => event.type === 'elite').length;

  for (const segment of segments) {
    const template = templates[segment.index];
    const segmentLength = segment.endDistance - segment.startDistance;
    const lane = ENEMY_LANES_4[randomInt(rng, 0, ENEMY_LANES_4.length - 1)];
    const triggerDistance = clamp(
      segment.startDistance + segmentLength * (0.45 + rng.next() * 0.25),
      segment.startDistance + 90,
      segment.endDistance - 80
    );

    let weaponBoxChance = template.weaponBoxChance;
    if (template.id === 'growthA' || template.id === 'relief') {
      weaponBoxChance = 1;
    } else if (template.id === 'peak') {
      weaponBoxChance = Math.max(weaponBoxChance, normalizedLevel >= 4 ? 0.72 : 0.52);
    }
    if (normalizedLevel <= 2) {
      weaponBoxChance = clamp(weaponBoxChance, 0.05, 1);
    } else if (normalizedLevel >= 4 && normalizedLevel <= 6) {
      weaponBoxChance = clamp(weaponBoxChance, 0.05, 1);
    } else if (normalizedLevel >= 8) {
      weaponBoxChance = clamp(weaponBoxChance + 0.12, 0.05, 1);
    }

    weaponBoxEvents.push({
      id: eventId++,
      triggerDistance,
      segmentId: segment.id,
      segmentIndex: segment.index,
      lane,
      chance: weaponBoxChance,
      expectedArmy: segment.expectedArmy,
      baseHp: computeWeaponBoxHp(segment.expectedArmy),
    });
  }

  const latestWeaponBoxTrigger = Math.round(
    getWeaponBoxLatestSpawnTimeSeconds(targetDurationSeconds) * scrollSpeed
  );
  for (const weaponBox of weaponBoxEvents) {
    weaponBox.triggerDistance = Math.min(weaponBox.triggerDistance, latestWeaponBoxTrigger);
  }

  // Every mission ends with a concrete power test. This event is added after
  // the conservative beatability guardrail so it cannot be silently removed
  // or downgraded into another one-HP enemy.
  const finaleSpawnSeconds = getFinaleSpawnTimeSeconds(targetDurationSeconds);
  const finaleSpawnDistance = Math.round(finaleSpawnSeconds * scrollSpeed);
  const finaleSegment = getSegmentForDistance(finaleSpawnDistance);
  const finaleEvent: CompiledEnemyEvent = {
    id: eventId++,
    triggerDistance: finaleSpawnDistance,
    segmentId: finaleSegment.id,
    segmentIndex: finaleSegment.index,
    lane: ENEMY_LANES_4[(normalizedLevel + normalizedTier) % ENEMY_LANES_4.length],
    type: 'elite',
    hp: getFinaleHp(normalizedLevel, bestPathMaxArmy, normalizedTier),
    isFinale: true,
  };
  enemyEvents.push(finaleEvent);

  // Preserve a readable silhouette around the mandatory target. If a regular
  // elite landed in the same screen window, keep it as support but downgrade
  // it to a basic enemy so the finale remains the unmistakable focus.
  const finaleEliteSpacing = scrollSpeed * 1.8;
  for (const event of enemyEvents) {
    if (event === finaleEvent || event.type !== 'elite') continue;
    if (Math.abs(event.triggerDistance - finaleEvent.triggerDistance) < finaleEliteSpacing) {
      event.type = 'basic';
      event.hp = undefined;
    }
  }

  targetEnemyCount = enemyEvents.length;
  targetEliteCount = enemyEvents.filter((event) => event.type === 'elite').length;

  if (normalizedLevel === 1) {
    const authored = buildAuthoredLevelOnePlan(
      segments,
      scrollSpeed,
      targetDurationSeconds,
      normalizedTier
    );
    enemyEvents.splice(0, enemyEvents.length, ...authored.enemyEvents);
    gateEvents.splice(0, gateEvents.length, ...authored.gateEvents);
    weaponBoxEvents.splice(0, weaponBoxEvents.length, ...authored.weaponBoxEvents);
    bestPathMaxArmy = authored.bestPathMaxArmy;
    gatePotentialStates = computeGatePotentialStates(gateEvents, GAMEPLAY_START.startingSoldiers);
    x2RowIndex = authored.x2RowIndex;
    targetEnemyCount = enemyEvents.length;
    targetEliteCount = enemyEvents.filter((event) => event.type === 'elite').length;

    for (const segment of segments) {
      const midProgress = clamp(
        (segment.startDistance + segment.endDistance) / 2 / trackLength,
        0,
        1
      );
      segment.expectedArmy = computeExpectedArmyAtProgress(
        bestPathMaxArmy,
        midProgress,
        GAMEPLAY_START.startingSoldiers
      );
    }
  }

  enemyEvents.sort((a, b) => a.triggerDistance - b.triggerDistance || a.id - b.id);
  gateEvents.sort((a, b) => a.triggerDistance - b.triggerDistance || a.id - b.id);
  weaponBoxEvents.sort((a, b) => a.triggerDistance - b.triggerDistance || a.id - b.id);

  return {
    level: normalizedLevel,
    tier: normalizedTier,
    seed: resolvedSeed,
    trackLength,
    targetDurationSeconds,
    scrollSpeed,
    segments,
    enemyEvents,
    gateEvents,
    weaponBoxEvents,
    bestPathMaxArmy,
    targetEnemyCount,
    targetEliteCount,
    x2RowIndex,
  };
}

export function getDeterministicRollForEvent(seed: number, eventId: number): number {
  return deterministicRoll(seed, eventId);
}
